import React, { useEffect, useMemo, useState } from 'react';
import HarbourModal from './HarbourModal';
import HarbourButton from './HarbourButton';
import HarbourNotice from './HarbourNotice';
import HarbourTextLink from './HarbourTextLink';
import { HarbourField } from './HarbourField';
import {
  apiHarbourAddTeacher, apiHarbourListSchoolTeachers, apiHarbourSetSchoolTeacherStatus,
} from '../../utils/rmsApi';
import {
  TEACHER_ROLE_MAX_LENGTH,
  TEACHER_SEARCH_MIN_LETTERS,
  filterSchoolTeachers,
  newTeacherErrors,
  teacherFullName,
} from '../../utils/schoolTeachers';
import './harbour.css';

const EMPTY_FORM = { firstName: '', lastName: '', email: '', jobRole: '', phone: '' };

/*
 * "Add a teacher" (TDI-616): search the school's teacher list and choose
 * one, or add a new teacher to the list. Either way the teacher is put on
 * the pupil's record and onAdded receives the server's answer
 * ({ participantId, schoolTeacherId, teacher, alreadyListed }), so the
 * record can go straight on to "{name}'s forms". Adding sends no email.
 *
 * Built from the existing Harbour/Modal, with result rows borrowed from the
 * SENCO "Your pupils" list (no Figma frame yet; Diana adds it after the
 * build). Wording is the ticket's.
 *
 * A teacher marked as no longer at the school (TDI-617) is listed under
 * "Show former teachers" with "Bring back", which puts them back on the
 * school's list (forms are not given back).
 */
export default function HarbourAddTeacherModal({ orgId, pupil, onClose, onAdded }) {
  const [step, setStep] = useState('search'); // 'search' | 'details'
  const [loadState, setLoadState] = useState('loading'); // 'loading' | 'ready' | 'failed'
  const [loadError, setLoadError] = useState(null);
  const [teachers, setTeachers] = useState([]);
  const [schoolName, setSchoolName] = useState('');
  const [query, setQuery] = useState('');
  const [showFormer, setShowFormer] = useState(false);
  const [choosingId, setChoosingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitted, setSubmitted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [duplicate, setDuplicate] = useState(null);
  const [failure, setFailure] = useState(null);
  const [bringingBackId, setBringingBackId] = useState(null);

  useEffect(() => {
    let live = true;
    apiHarbourListSchoolTeachers({ orgId, patientRecordId: pupil.id, includeFormer: true })
      .then((res) => {
        if (!live) return;
        setTeachers((res && res.teachers) || []);
        setSchoolName((res && res.schoolName) || '');
        setLoadState('ready');
      })
      .catch((e) => {
        if (!live) return;
        setLoadError(String(e?.code || '').includes('failed-precondition') ? e.message : null);
        setLoadState('failed');
      });
    return () => { live = false; };
  }, [orgId, pupil.id]);

  const results = useMemo(
    () => filterSchoolTeachers(teachers, query, { includeFormer: showFormer }),
    [teachers, query, showFormer],
  );
  const searching = query.trim().length >= TEACHER_SEARCH_MIN_LETTERS;
  const school = schoolName || 'your school';
  const errors = submitted ? newTeacherErrors(form) : {};

  const add = async (payload) => {
    setFailure(null);
    const res = await apiHarbourAddTeacher({ orgId, patientRecordId: pupil.id, ...payload });
    if (res && res.ok === false && res.code === 'ALREADY_ON_SCHOOL_LIST') {
      setDuplicate(res.teacher || null);
      return;
    }
    onAdded(res);
  };

  const choose = async (teacher) => {
    setChoosingId(teacher.schoolTeacherId);
    try {
      await add({ schoolTeacherId: teacher.schoolTeacherId });
    } catch (e) {
      setFailure(e?.message || 'The teacher could not be added. Check your connection and try again.');
    } finally {
      setChoosingId(null);
    }
  };

  const submitNew = async () => {
    setSubmitted(true);
    setDuplicate(null);
    if (Object.keys(newTeacherErrors(form)).length) return;
    setBusy(true);
    try {
      await add({
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        email: form.email.trim(),
        jobRole: form.jobRole.trim(),
        phone: form.phone.trim() || null,
      });
    } catch (e) {
      setFailure(e?.message || 'The teacher could not be added. Check your connection and try again.');
    } finally {
      setBusy(false);
    }
  };

  const useDuplicate = async () => {
    if (!duplicate) return;
    setBusy(true);
    try {
      await add({ schoolTeacherId: duplicate.schoolTeacherId });
    } catch (e) {
      setFailure(e?.message || 'The teacher could not be added. Check your connection and try again.');
    } finally {
      setBusy(false);
    }
  };

  // "Bring back" (TDI-617): active again on the school's list, so they can
  // be chosen. Nothing they held before is given back.
  const bringBack = async (teacher) => {
    setFailure(null);
    setBringingBackId(teacher.schoolTeacherId);
    try {
      await apiHarbourSetSchoolTeacherStatus({
        orgId, patientRecordId: pupil.id, schoolTeacherId: teacher.schoolTeacherId, status: 'active',
      });
      setTeachers((list) => list.map((t) => (t.schoolTeacherId === teacher.schoolTeacherId
        ? { ...t, status: 'active' } : t)));
      if (duplicate && duplicate.schoolTeacherId === teacher.schoolTeacherId) {
        setDuplicate({ ...duplicate, status: 'active' });
      }
    } catch (e) {
      setFailure(e?.message || 'That did not work. Check your connection and try again.');
    } finally {
      setBringingBackId(null);
    }
  };

  const setField = (field) => (e) => {
    const { value } = e.target;
    setForm((f) => ({ ...f, [field]: value }));
    if (field === 'email') setDuplicate(null);
  };

  if (step === 'details') {
    const duplicateName = teacherFullName(duplicate) || (duplicate && duplicate.email);
    return (
      <HarbourModal
        title="Add a new teacher"
        confirmLabel="Add teacher"
        busy={busy}
        onConfirm={submitNew}
        onClose={onClose}
      >
        <div className="flex flex-col gap-4 text-left">
          <p className="harbour-body-16">
            {`They'll be added to ${school}'s list, so you can choose them for other pupils too.`}
          </p>
          <HarbourField
            label="First name"
            value={form.firstName}
            autoComplete="off"
            error={errors.firstName}
            onChange={setField('firstName')}
          />
          <HarbourField
            label="Last name"
            value={form.lastName}
            autoComplete="off"
            error={errors.lastName}
            onChange={setField('lastName')}
          />
          <HarbourField
            label="Email address"
            hint="We send their sign-in email here. Use their school email if they have one."
            type="email"
            inputMode="email"
            autoCapitalize="none"
            spellCheck={false}
            autoComplete="off"
            value={form.email}
            error={errors.email}
            onChange={setField('email')}
          />
          <HarbourField
            label="Role"
            hint="For example, Year 3 class teacher or maths teacher"
            maxLength={TEACHER_ROLE_MAX_LENGTH}
            autoComplete="off"
            value={form.jobRole}
            error={errors.jobRole}
            onChange={setField('jobRole')}
          />
          <HarbourField
            label="Phone number"
            hint="Optional"
            type="tel"
            autoComplete="off"
            value={form.phone}
            onChange={setField('phone')}
          />
          {duplicate && (
            <HarbourNotice kind="warning" title={`${duplicateName} is already on your school's list`}>
              <div className="pt-2">
                {(duplicate.status || 'active') === 'active' ? (
                  <HarbourButton kind="secondary" busy={busy} disabled={busy} onClick={useDuplicate}>
                    {`Use ${duplicateName} instead`}
                  </HarbourButton>
                ) : (
                  <HarbourButton
                    kind="secondary"
                    busy={bringingBackId === duplicate.schoolTeacherId}
                    disabled={Boolean(bringingBackId)}
                    onClick={() => bringBack(duplicate)}
                  >
                    Bring back
                  </HarbourButton>
                )}
              </div>
            </HarbourNotice>
          )}
          {failure && (
            <HarbourNotice kind="error" title="That did not work">{failure}</HarbourNotice>
          )}
        </div>
      </HarbourModal>
    );
  }

  const eyebrow = [pupil.name, pupil.yearGroup].filter(Boolean).join(' ');
  return (
    <HarbourModal
      title="Add a teacher"
      eyebrow={eyebrow}
      showConfirm={false}
      busy={Boolean(choosingId)}
      onClose={onClose}
    >
      <div className="flex flex-col gap-4 text-left">
        <p className="harbour-body-16">
          Search your school&apos;s teachers. If they&apos;re not on the list yet, you can add them.
        </p>
        <HarbourField
          label="Search by name"
          hint="Type at least two letters of their first or last name"
          type="search"
          autoComplete="off"
          spellCheck={false}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <label className="harbour-teacher-search__former harbour-body-16">
          <input
            type="checkbox"
            checked={showFormer}
            onChange={(e) => setShowFormer(e.target.checked)}
          />
          <span>Show former teachers</span>
        </label>

        {loadState === 'loading' && (
          <div aria-busy="true" className="flex flex-col gap-3">
            <div className="harbour-skeleton h-[64px] animate-pulse" aria-hidden="true" />
            <div className="harbour-skeleton h-[64px] animate-pulse" aria-hidden="true" />
          </div>
        )}
        {loadState === 'failed' && (
          <HarbourNotice kind="error" title="That did not work">
            {loadError || "Your school's teachers could not be loaded. Check your connection and try again."}
          </HarbourNotice>
        )}
        {loadState === 'ready' && results.length > 0 && (
          <ul className="harbour-teacher-search__results" aria-label="Teachers">
            {results.map((t) => {
              const name = teacherFullName(t) || t.email;
              const former = (t.status || 'active') !== 'active';
              return (
                <li key={t.schoolTeacherId} className="harbour-teacher-search__row">
                  <div className="harbour-teacher-search__facts">
                    <span className="harbour-teacher-search__name">{name}</span>
                    <span className="harbour-teacher-search__meta">{t.jobRole || 'Role not added yet'}</span>
                    <span className="harbour-teacher-search__meta">{t.email}</span>
                    {former && (
                      <span className="harbour-teacher-search__meta">No longer at this school</span>
                    )}
                  </div>
                  {former && (
                    <HarbourButton
                      kind="secondary"
                      busy={bringingBackId === t.schoolTeacherId}
                      disabled={Boolean(bringingBackId) || Boolean(choosingId)}
                      aria-label={`Bring back ${name}`}
                      onClick={() => bringBack(t)}
                    >
                      Bring back
                    </HarbourButton>
                  )}
                  {!former && (
                    <HarbourButton
                      kind="secondary"
                      busy={choosingId === t.schoolTeacherId}
                      disabled={Boolean(choosingId)}
                      aria-label={`Choose ${name}`}
                      onClick={() => choose(t)}
                    >
                      Choose
                    </HarbourButton>
                  )}
                </li>
              );
            })}
          </ul>
        )}
        {loadState === 'ready' && searching && results.length === 0 && (
          <p className="harbour-body-16" role="status">
            {`No teachers at ${school} match '${query.trim()}'.`}
          </p>
        )}
        {failure && (
          <HarbourNotice kind="error" title="That did not work">{failure}</HarbourNotice>
        )}
        <HarbourTextLink
          size="standard"
          className="self-start"
          disabled={Boolean(choosingId)}
          onClick={() => { setFailure(null); setStep('details'); }}
        >
          Can&apos;t find them? Add a new teacher
        </HarbourTextLink>
      </div>
    </HarbourModal>
  );
}
